import { DialogM } from "@common/components/alert/dialog/Dialog"
import IconButtonM from "@common/components/button/IconButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import withPreventDoubleClick from "@common/utils/withPreventDoubleClick"
import MobileDatePickerModal from "@common/components/modal/MobileDatePickerModal"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import {
    JUNE_TRANSITION,
    SUMMER_CHOICE_STATUS,
    SUMMER_MONTH_CHOICE,
    SUMMER_MONTHS,
    SUMMER_RESUME_COMMITMENT,
    SUMMER_YEARLY_COMMITMENT_DISCOUNT,
    deriveResumePreferenceFromConfig,
    deriveResumeOfferTypeFromConfig,
    deriveSummerStatusFromChoices,
    getCurrentFormulaLabel,
    getEffectiveSummerPauseCoverage,
    getEligibleSummerMonths,
    getStudentFirstName,
    getSummerConfigSafe,
    getSummerJuneBounds,
    getSummerMonthChoiceValue,
    getSummerMonthLabel,
    getSummerYearFromStaticData,
    hasSummerChoiceSet,
    isCommitmentBeyondSummerProjected,
    isMonthCardEditable,
    meetsRenewalDiscountFrequency,
    projectCommitmentEndDate,
    shouldOfferRenewalDiscount,
} from "@common/utils/summer2026"
import {
    confirmSummerChoices,
    markSummerCockpitViewed,
    resetSummerChoice,
    setJunePause,
    setSummerMonthChoice,
    setSummerResumeConfig,
} from "@data/redux/actions/summer"
import { apiRequest } from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import { subscriptionService } from "@data/services/subscriptionService"
import { useNavigation } from "@react-navigation/native"
import { LinearGradient } from "expo-linear-gradient"
import moment from "moment"
import "moment/locale/fr"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import {
    ActivityIndicator,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import { List } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import SummerMonthEditModal from "./SummerMonthEditModal"
import SummerResumeEditModal from "./SummerResumeEditModal"
import { SummerMonthPlanCard, SummerResumePlanCard } from "./SummerPlanCards"

moment.locale("fr")

const DESKTOP_BREAKPOINT = 900
const CARD_GAP = 16
const COMMITMENT_HALF = SUMMER_YEARLY_COMMITMENT_DISCOUNT / 2

const FAQ_ITEMS = [
    {
        icon: "calendar-day",
        title: "Que faire entre aujourd'hui et le 30 juin ?",
        body: "Si votre rythme actuel ne vous convient pas pour la fin d'année, vous pouvez mettre en pause votre abonnement, augmenter le nombre d'heures ou passer en Mode Autonome. Rendez-vous dans le menu Gestion de votre abonnement.",
    },
    {
        icon: "sun",
        title: "Comment marche le pack Cours d'été à -50% ?",
        body: "Pour 89€/mois au lieu de 169€, votre enfant bénéficie de 3 séances hebdo en petit groupe, au choix parmi des créneaux du matin (10h30-11h30) ou du soir (18h30-19h30) du lundi au vendredi, accompagnées d'un programme personnalisé et d'énoncés IA illimités.",
    },
    {
        icon: "redo",
        title: "Puis-je modifier mes choix après confirmation ?",
        body: "Oui, jusqu'au 31 août. Vous pouvez revenir sur ce programme d'été à tout moment depuis votre portail abonnement.",
    },
    {
        icon: "user-graduate",
        title: "Que comprend le Mode Autonome ?",
        body: "Plateforme et IA pédagogique illimitées (énoncés à la demande, corrections instantanées) + 1 cours offert avec un tuteur dans le mois. 19€/mois sans engagement.",
    },
    {
        icon: "pause-circle",
        title: "Que se passe-t-il si je choisis Pause ?",
        body: "Aucun prélèvement, aucune séance pour le mois choisi. L'abonnement reprend automatiquement à la date que vous avez configurée en septembre.",
    },
    {
        icon: "gift",
        title: `Comment fonctionnent les ${SUMMER_YEARLY_COMMITMENT_DISCOUNT}€ offerts à la rentrée ?`,
        body: `${COMMITMENT_HALF}€ offerts sur votre premier mois de rentrée puis ${COMMITMENT_HALF}€ sur le deuxième mois — soit ${SUMMER_YEARLY_COMMITMENT_DISCOUNT}€ étalés sur les deux premiers prélèvements. Offre réservée au réengagement 12 mois à partir de 2h/sem, pour les parents dont l'engagement actuel se termine avant la rentrée.`,
    },
]

const getDefaultSubjectIds = (user) => {
    const prioritized = user?.prioritized_school_subjects
    if (Array.isArray(prioritized) && prioritized.length > 0) {
        return prioritized.map((s) => s.id)
    }
    return []
}

// Projects a Redux `SUMMER_MONTH_CHOICE` (local slot value) onto the
// `SUMMER_CHOICE_STATUS` shape persisted on UserResource
// (`summer_july_status` / `summer_august_status`). Shared between the
// confirm payload builder and the local-vs-backend divergence check used
// by handleExitRequest.
const monthChoiceToSummerStatus = (choice) => {
    switch (choice) {
        case SUMMER_MONTH_CHOICE.SUMMER_COURSES:
            return SUMMER_CHOICE_STATUS.CHOSE_SUMMER_COURSES
        case SUMMER_MONTH_CHOICE.AUTONOMY:
            return SUMMER_CHOICE_STATUS.CHOSE_AUTONOMY
        case SUMMER_MONTH_CHOICE.PAUSE:
            return SUMMER_CHOICE_STATUS.CHOSE_PAUSE
        default:
            return null
    }
}

// Canonical string form for ID arrays — order-insensitive (the cockpit
// stores subjectIds / slots_possible as plain arrays but two arrays with
// the same elements in a different order describe the same selection).
const canonicalIdArray = (ids) =>
    Array.isArray(ids) && ids.length
        ? JSON.stringify([...ids].map(String).sort())
        : "[]"

// Canonical string form for a per-month coursesConfig blob, used by the
// divergence check so a parent who edits only the offre / créneaux /
// h-sem / matières (without flipping the coarse month status) still
// triggers the unsaved-changes dialog on exit. `null` and the empty
// shape collapse to the same canonical form so a freshly-cleared config
// reads as equal to a backend-side null.
const canonicalCoursesConfig = (config) => {
    if (!config || typeof config !== "object") return "null"
    const offerType = config.offerType ?? null
    const hoursPerWeek = config.hoursPerWeek ?? null
    const subjectIds = canonicalIdArray(config.subjectIds)
    const slotsPossible = canonicalIdArray(config.slots_possible)
    if (
        offerType == null &&
        hoursPerWeek == null &&
        subjectIds === "[]" &&
        slotsPossible === "[]"
    ) {
        return "null"
    }
    return JSON.stringify({
        offerType,
        hoursPerWeek,
        subjectIds,
        slotsPossible,
    })
}

const SummerCockpitScreen = (props) => {
    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)

    const julyRef = useRef(null)
    const augustRef = useRef(null)
    const resumeRef = useRef(null)
    const [faqVisible, setFaqVisible] = useState(false)
    const [junePickerVisible, setJunePickerVisible] = useState(false)

    useEffect(() => {
        props.markSummerCockpitViewed()
        // Charge la balance pause — sert au safety check Case 3 :
        // `current_pause.previous_commitment_end_date` permet de ne pas
        // wiper le slot Rentrée quand l'engagement courant survivait
        // déjà au 31/08 avant le report de pause (cas vrai engagement
        // annuel).
        props.apiRequest(subscriptionService.getParentPauseBalance)
        // Charge le catalogue (matières, grille tarifaire, séances/sem) dont
        // dépendent le picker de matières Cours d'été et la carte Rentrée.
        // Indispensable en accès direct au cockpit (deep link) : sans passer
        // par le portail, `formData.schoolSubjects` reste vide et aucune
        // matière n'est sélectionnable.
        props.apiRequest(subscriptionService.getSubscribePricing)
        // markSummerCockpitViewed est idempotent — un seul appel au mount
        // suffit, pas de dépendance à tracker.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Attendre la persistence backend avant de fermer le cockpit. Si le
    // POST échoue, useApiStatus affiche le dialog d'erreur et le bouton
    // sort de son loading state — l'utilisateur peut réessayer.
    useApiStatus(subscriptionService.confirmSummerChoice, null, true, () => {
        navigation.goBack()
        props.apiRequest(authService.getUser)
        props.apiRequest(subscriptionService.getParentPauseBalance)
        props.apiRequest(subscriptionService.getSubscribePricing)
    })

    const firstName = getStudentFirstName(props.user) || "votre enfant"
    const summer = props.summer ?? {}
    const summerYear = props.summerYear
    const summerConfig = props.summerConfig
    const summerCfg = getSummerConfigSafe(summerConfig)

    // June block — read-only continuation of the current formula by default,
    // with an optional advanced pause (1 → 30 June). June NEVER gates the
    // cockpit confirm: it stays a side decision on top of the July/August/
    // rentrée trajectory. `junePauseStartDate` (ISO) drives the read-only card
    // state and the confirm payload.
    const { juneStart: juneWindowStart, juneEnd: juneMaxDate } =
        getSummerJuneBounds(summerConfig)
    // Picker min never lets the parent advance a pause into the past: clamp the
    // canonical June start (01/06) up to tomorrow, mirroring the manual pause
    // modal's `today + 1` floor.
    const juneMinDate = moment.max(
        juneWindowStart,
        moment().add(1, "day").startOf("day")
    )
    // Once the summer window is open (01/07+) June is in the past — the block is
    // a pre-window affordance only. `canAdvanceJunePause` also drops the CTA when
    // no June day is left to pick (e.g. 30/06, where the floor is already 01/07).
    const beforeSummerWindow = moment().isBefore(
        moment(summerCfg.windowStart).startOf("day")
    )
    const canAdvanceJunePause = juneMinDate.isSameOrBefore(juneMaxDate)
    const junePauseStartDate = summer?.junePauseStartDate ?? null
    // Once a confirmed June pause's start date is in the past it has begun and
    // can no longer be edited or cancelled — lock the block read-only. Uses the
    // saved value so an unconfirmed draft never locks.
    const savedJunePauseStartDate =
        props.user?.summer_june_pause_start_date ?? null
    const juneChoiceLocked =
        !!savedJunePauseStartDate &&
        moment(savedJunePauseStartDate).isSameOrBefore(moment().startOf("day"))
    const currentFormulaLabel = getCurrentFormulaLabel(props.user)
    const openJunePicker = () => setJunePickerVisible(true)
    const cancelJunePause = () => props.setJunePause(null)
    const confirmJunePause = (isoDate) => {
        setJunePickerVisible(false)
        props.setJunePause(isoDate)
    }
    // A persisted draft can hold a June start that has slipped into the past
    // (picked, never confirmed) and would fail backend validation — drop it to
    // the default. Never a locked (confirmed, started) pause: it stays read-only.
    const juneMinDateIso = juneMinDate.format("YYYY-MM-DD")
    useEffect(() => {
        if (!junePauseStartDate || juneChoiceLocked) return
        if (moment(junePauseStartDate).isBefore(moment(juneMinDateIso))) {
            props.setJunePause(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [junePauseStartDate, juneMinDateIso, juneChoiceLocked])

    // Stable identity so a data-fetch re-render (new array) can't retrigger the
    // edit modals' reset effect and wipe in-progress edits. Keyed on the
    // prioritized subject ids — changes only when they actually do.
    const prioritizedSubjectKey = (
        props.user?.prioritized_school_subjects ?? []
    )
        .map((s) => s.id)
        .join(",")
    const defaultSubjectIds = useMemo(
        () => getDefaultSubjectIds(props.user),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [prioritizedSubjectKey]
    )

    const isDesktop =
        (dimensions?.width ?? 0) >= DESKTOP_BREAKPOINT &&
        !dimensions?.smallScreen

    // Cap content width — single-column stacked layout on all screen sizes,
    // matches the portal (640 max). The cards span full inner width.
    const contentMaxWidth = 640
    const contentWidth =
        (dimensions?.width ?? 0) < 800
            ? dimensions?.width
            : Math.min(contentMaxWidth, dimensions?.width || contentMaxWidth)

    const cardWidth = "100%"

    // Verrouillage par mois : un mois passé en cours (juillet à partir du
    // 1/07, août à partir du 1/08) n'est plus éditable. Le bouton "Modifier"
    // de la card affiche alors un message d'info plutôt que d'ouvrir le
    // modal. Le mois à venir reste éditable même quand la fenêtre est
    // active (ex: pendant juillet, on peut encore configurer août).
    // Authoritative lock from the backend (same boundary the confirm endpoint
    // enforces); falls back to the local calendar computation when the field is
    // absent (older payloads / before the user is loaded).
    const julyLocked =
        props.user?.summer_july_locked ??
        !isMonthCardEditable(SUMMER_MONTHS.JULY, undefined, summerConfig)
    const augustLocked =
        props.user?.summer_august_locked ??
        !isMonthCardEditable(SUMMER_MONTHS.AUGUST, undefined, summerConfig)

    // Pause manuelle déjà programmée qui chevauche la fenêtre été. Pour
    // ces mois, on bypass le modal d'édition (c'est la pause Stripe en
    // place qui décide) et on affiche un état read-only "Pause déjà
    // programmée du X au Y".
    const pauseCoverage = useMemo(() => {
        const raw = getEffectiveSummerPauseCoverage(props.user, summerConfig)
        const julyLocal = getSummerMonthChoiceValue(props.summer?.july)
        const augustLocal = getSummerMonthChoiceValue(props.summer?.august)
        const LOCAL_OVERRIDES = new Set([
            SUMMER_MONTH_CHOICE.SUMMER_COURSES,
            SUMMER_MONTH_CHOICE.AUTONOMY,
            SUMMER_MONTH_CHOICE.PAUSE,
        ])
        return {
            ...raw,
            julyCovered: raw.julyCovered && !LOCAL_OVERRIDES.has(julyLocal),
            augustCovered:
                raw.augustCovered && !LOCAL_OVERRIDES.has(augustLocal),
        }
    }, [props.user, props.summer?.july, props.summer?.august, summerConfig])
    const formatPauseRange = (start, end) => {
        if (!start || !end) return null
        return `Pause déjà programmée du ${moment(start).format(
            "D MMMM"
        )} au ${moment(end).format("D MMMM YYYY")}`
    }
    const julyForcedReason = pauseCoverage.julyCovered
        ? formatPauseRange(
              pauseCoverage.manualPauseStartDate,
              pauseCoverage.manualPauseEndDate
          )
        : null
    const augustForcedReason = pauseCoverage.augustCovered
        ? formatPauseRange(
              pauseCoverage.manualPauseStartDate,
              pauseCoverage.manualPauseEndDate
          )
        : null

    const showLockedDialog = (month) => {
        const label = getSummerMonthLabel(month)
        DialogM.show({
            text1: `${label} est en cours`,
            text2: `Ce mois ne peut plus être modifié — il a déjà commencé. Vous pouvez encore ajuster les mois à venir et votre rentrée.`,
            buttonText1: "Compris",
            onPress: () => {},
            exitButton: true,
            onPressBack: () => {},
        })
    }

    const openJuly = () => {
        if (julyForcedReason) return
        if (julyLocked) {
            showLockedDialog(SUMMER_MONTHS.JULY)
            return
        }
        julyRef.current?.present?.()
    }
    const openAugust = () => {
        if (augustForcedReason) return
        if (augustLocked) {
            showLockedDialog(SUMMER_MONTHS.AUGUST)
            return
        }
        augustRef.current?.present?.()
    }
    const openResume = () => resumeRef.current?.present?.()

    const eligibleMonths = useMemo(() => {
        const raw = getEligibleSummerMonths(props.user, summerConfig)
        const julyLocal = getSummerMonthChoiceValue(props.summer?.july)
        const augustLocal = getSummerMonthChoiceValue(props.summer?.august)
        const LOCAL_OVERRIDES = new Set([
            SUMMER_MONTH_CHOICE.SUMMER_COURSES,
            SUMMER_MONTH_CHOICE.AUTONOMY,
            SUMMER_MONTH_CHOICE.PAUSE,
        ])
        const set = new Set(raw)
        if (LOCAL_OVERRIDES.has(julyLocal)) set.add(SUMMER_MONTHS.JULY)
        if (LOCAL_OVERRIDES.has(augustLocal)) set.add(SUMMER_MONTHS.AUGUST)
        return Array.from(set)
    }, [props.user, props.summer?.july, props.summer?.august, summerConfig])
    const julyEligible = eligibleMonths.includes(SUMMER_MONTHS.JULY)
    const augustEligible = eligibleMonths.includes(SUMMER_MONTHS.AUGUST)
    const plannedResumeDate = props.user?.subscription_state?.planned_start_date

    const julyChoice = getSummerMonthChoiceValue(summer.july)
    const augustChoice = getSummerMonthChoiceValue(summer.august)

    // Projection de la date de fin d'engagement post-confirm. On part de
    // la date d'engagement PRÉ-PAUSE-D'ÉTÉ et on ajoute 31 jours par
    // mois pausé localement. Le résultat reflète ce que le parent VERRA
    // après avoir tapé Confirmer ; il drive le verrou yearly_12m et le
    // label "Votre engagement se termine le X" dans le modal Rentrée.
    //
    // Base = previous_commitment_end_date UNIQUEMENT quand la pause en
    // flight est une `summer_auto_pause` (on veut alors annuler l'effet
    // déjà appliqué côté backend pour ne pas double-compter). Sinon
    // (pas de pause, ou pause manuelle non-été), on lit
    // `user.commitment_ends_at` directement.
    const currentPause = props.pauseBalance?.current_pause
    const baseCommitmentEndDate =
        currentPause?.reason === "summer_auto_pause" &&
        currentPause?.previous_commitment_end_date
            ? currentPause.previous_commitment_end_date
            : (props.user?.commitment_ends_at ?? null)
    const projectedCommitmentEndDate = useMemo(
        () =>
            projectCommitmentEndDate(
                baseCommitmentEndDate,
                julyChoice,
                augustChoice
            ),
        [baseCommitmentEndDate, julyChoice, augustChoice]
    )
    const commitmentLockedYearly = useMemo(
        () =>
            isCommitmentBeyondSummerProjected(
                projectedCommitmentEndDate,
                undefined,
                summerConfig
            ),
        [projectedCommitmentEndDate, summerConfig]
    )

    // Le gift box "100€ offerts" et le ruban "🎁" de la carte Rentrée ne
    // s'affichent que si :
    //   1. Réengagement effectif à la rentrée — engagement projeté qui se
    //      termine AVANT la date de reprise (ou pas d'engagement). Un
    //      engagement déjà étiré au-delà de la fenêtre (ex. 12/07 → 12/09)
    //      ne se réengage pas → pas de remise ;
    //   2. Le parent a coché l'engagement 12 mois côté Reprise ;
    //   3. La fréquence choisie est ≥ 2h/sem (1h/sem exclue).
    // L'éligibilité lit la projection cockpit : un parent dont le backend
    // commit est post-fenêtre (auto-pause en flight) mais qui retire ses
    // pauses draft retombe sous le cut-off et redevient éligible.
    const renewalDiscountEligible = shouldOfferRenewalDiscount(
        props.user,
        summerConfig,
        projectedCommitmentEndDate
    )
    const showCommitmentGift =
        renewalDiscountEligible &&
        summer?.resumeCommitment === SUMMER_RESUME_COMMITMENT.YEARLY_12M &&
        meetsRenewalDiscountFrequency(summer?.resumeFrequency)

    // Cohort C2 : si un mois n'est pas éligible (déjà couvert par une
    // pause active) OU est forcé en read-only via une pause manuelle qui
    // chevauche la fenêtre, il est traité comme "décidé" automatiquement.
    // Pour les C2 stricts (les deux mois neutralisés), seul le choix
    // resume_preference (resumeType) est requis pour pouvoir confirmer.
    const everyMonthDecided =
        (!julyEligible || !!julyChoice || !!julyForcedReason) &&
        (!augustEligible || !!augustChoice || !!augustForcedReason)
    const resumeDecided = !!summer?.resumeType
    const canConfirm = everyMonthDecided && resumeDecided
    const hasAnyChoice = hasSummerChoiceSet(summer) || !!summer?.resumeType
    // Cohorte C2 stricte : pause manuelle couvre toute la fenêtre. Aucun
    // choix mensuel n'est exposé/possible — seul le resume_preference
    // marque la finalisation. Le summer_status envoyé doit refléter cette
    // trajectoire pause (cohérent avec leur situation factuelle).
    const isC2FullyCovered =
        pauseCoverage.julyCovered && pauseCoverage.augustCovered

    // Dernière trajectoire SAUVEGARDÉE (= ce que le parent a confirmé la
    // fois précédente, exposée par UserResource). Sert de référence à la
    // divergence "saved-then-modified" du handleExitRequest. `auto_paused`
    // est normalisé en `chose_pause` côté comparaison : le draft local ne
    // porte que le choix coarse `pause` (cf. deriveMonthSlotsFromStatus),
    // donc sans normalisation on déclencherait un faux positif à chaque
    // ré-ouverture pour un parent auto-paused. Fallback sur le statut
    // legacy `summer_summer_status` calqué sur `hydrateFromUser` : quand
    // les per-month columns sont null mais le statut legacy est posé
    // (admin override BO, ancien backfill), la divergence doit lire la
    // même valeur que celle qui a hydraté Redux, sinon faux positif sur
    // chaque re-open.
    const normalizeSavedMonthStatus = (status) =>
        status === SUMMER_CHOICE_STATUS.AUTO_PAUSED
            ? SUMMER_CHOICE_STATUS.CHOSE_PAUSE
            : status
    const savedLegacyStatus =
        props.user?.summer_summer_status ?? props.user?.summer_choice_status
    const savedJulyStatus = normalizeSavedMonthStatus(
        props.user?.summer_july_status ?? savedLegacyStatus
    )
    const savedAugustStatus = normalizeSavedMonthStatus(
        props.user?.summer_august_status ?? savedLegacyStatus
    )
    const savedResumePreference = props.user?.summer_resume_preference
    const savedResumeOfferType = props.user?.summer_resume_offer_type
    const savedResumeCommitment = props.user?.summer_resume_commitment
    const savedJulyCoursesConfig = props.user?.summer_july_courses_config
    const savedAugustCoursesConfig = props.user?.summer_august_courses_config
    const savedResumeSubjectIds = props.user?.summer_resume_subject_ids

    // Case 2 — divergence du brouillon local vs trajectoire sauvegardée,
    // axe par axe. On compare les VALEURS effectives (juillet/août projetés
    // vers SUMMER_CHOICE_STATUS, resume_preference dérivée du resumeType
    // local, resume_offer_type dérivé, resume_commitment forcé yearly_12m
    // quand l'engagement courant est déjà au-delà du 31/08, pause juin
    // avancée). Une trajectoire confirmée puis re-éditée sans avoir reconfirmé
    // déclenche le dialog d'avertissement sur le X.
    const localDraftDivergesFromSaved = useMemo(() => {
        if (!summer?.decidedAt) return false

        // June advanced-pause axis — null vs a date are different trajectories.
        if ((summer.junePauseStartDate ?? null) !== savedJunePauseStartDate) {
            return true
        }

        const localJulyStatus = monthChoiceToSummerStatus(julyChoice)
        const localAugustStatus = monthChoiceToSummerStatus(augustChoice)
        if (localJulyStatus !== savedJulyStatus) return true
        if (localAugustStatus !== savedAugustStatus) return true

        const localResumePreference = summer.resumeType
            ? deriveResumePreferenceFromConfig({
                  type: summer.resumeType,
                  frequency: summer.resumeFrequency,
              })
            : null
        if (localResumePreference !== savedResumePreference) return true

        // Offer type — `summer_resume_offer_type` n'est posé qu'à partir
        // du cockpit confirm, donc les confirmations legacy peuvent
        // l'avoir null. On ne signale divergence que si les deux côtés
        // portent une opinion explicite ; sinon on s'aligne sur les
        // autres axes.
        const localResumeOfferType = summer.resumeType
            ? deriveResumeOfferTypeFromConfig({ type: summer.resumeType })
            : null
        if (
            localResumeOfferType != null &&
            savedResumeOfferType != null &&
            localResumeOfferType !== savedResumeOfferType
        ) {
            return true
        }

        // Compare la valeur Redux stockée à la valeur sauvegardée.
        // L'override d'affichage `commitmentLockedYearly` est runtime —
        // l'appliquer ici déclencherait un faux positif quand la
        // trajectoire sauvegardée a glissé suite à l'application d'une
        // pause sans que le parent ait modifié quoi que ce soit dans le
        // cockpit. On gate aussi le côté saved : les parents qui ont
        // confirmé avant la migration `resume_commitment` ont saved=null
        // tandis que le slice local hydrate à "monthly" par défaut, ce
        // qui ferait fire la divergence sur une simple ré-ouverture.
        if (
            summer.resumeCommitment != null &&
            savedResumeCommitment != null &&
            summer.resumeCommitment !== savedResumeCommitment
        ) {
            return true
        }

        // Per-month coursesConfig — comparé uniquement quand le mois local
        // est en SUMMER_COURSES (sinon le payload envoie null et le check
        // de statut plus haut a déjà tranché). Couvre les édits granulaires
        // offre / créneaux / h-sem / matières au sein d'un mois Cours d'été.
        if (julyChoice === SUMMER_MONTH_CHOICE.SUMMER_COURSES) {
            const localJulyConfig =
                summer.july && typeof summer.july !== "string"
                    ? (summer.july.coursesConfig ?? null)
                    : null
            if (
                canonicalCoursesConfig(localJulyConfig) !==
                canonicalCoursesConfig(savedJulyCoursesConfig)
            ) {
                return true
            }
        }
        if (augustChoice === SUMMER_MONTH_CHOICE.SUMMER_COURSES) {
            const localAugustConfig =
                summer.august && typeof summer.august !== "string"
                    ? (summer.august.coursesConfig ?? null)
                    : null
            if (
                canonicalCoursesConfig(localAugustConfig) !==
                canonicalCoursesConfig(savedAugustCoursesConfig)
            ) {
                return true
            }
        }

        // Rentrée subject overrides — comparé order-insensitive, traite
        // null / [] comme équivalents pour ne pas signaler divergence sur
        // une trajectoire legacy qui n'avait jamais posé d'override.
        if (
            canonicalIdArray(summer.resumeSubjectIds) !==
            canonicalIdArray(savedResumeSubjectIds)
        ) {
            return true
        }

        return false
    }, [
        summer?.decidedAt,
        julyChoice,
        augustChoice,
        summer.july,
        summer.august,
        summer.resumeType,
        summer.resumeFrequency,
        summer.resumeCommitment,
        summer.resumeSubjectIds,
        summer.junePauseStartDate,
        savedJulyStatus,
        savedAugustStatus,
        savedResumePreference,
        savedResumeOfferType,
        savedResumeCommitment,
        savedJulyCoursesConfig,
        savedAugustCoursesConfig,
        savedResumeSubjectIds,
        savedJunePauseStartDate,
    ])

    // Case 3 — détection de la transition lock true → false sur le verrou
    // PROJETÉ. Le verrou projeté monte/descend selon `julyChoice` et
    // `augustChoice` locaux (cf. `projectedCommitmentEndDate` plus haut) :
    // si le parent retire les pauses qui poussaient l'engagement après le
    // 31/08, la projection retombe avant le 31/08 et la projection bascule
    // false. Comme la projection part de la date PRE-pause
    // (`current_pause.previous_commitment_end_date` quand une pause d'été
    // est en flight, sinon `user.commitment_ends_at` directement), un
    // vrai engagement annuel n'est jamais affecté : le verrou reste true
    // quelles que soient les pauses, donc pas de wipe parasite.
    //
    // Mount-time race : tant que `pauseBalance` n'est pas chargé, la base
    // de projection retombe sur `user.commitment_ends_at` (déjà shifté par
    // la pause backend) au lieu de `previous_commitment_end_date`. Le
    // verrou calculé est alors faussement true, puis bascule false dès que
    // pauseBalance arrive — exactement la transition qui déclencherait un
    // wipe spurious. On défère donc l'initialisation du ref ET le check de
    // wipe jusqu'à ce que pauseBalance soit chargé.
    const pauseBalanceLoaded = props.pauseBalance != null
    const prevCommitmentLockedYearlyRef = useRef(null)
    const wipeInitializedRef = useRef(false)

    // Projection "no-user-action" — ce que le verrou serait si le draft
    // local matchait exactement la trajectoire sauvegardée. Sert de
    // baseline anti-race au post-load init : si une édition draft a
    // atterri pendant le round-trip de getParentPauseBalance, la
    // projection sauvegardée est encore lockée tandis que la projection
    // locale ne l'est plus — c'est la même transition wipe-worthy que le
    // compare incrémental détecte, juste déphasée. On la rattrape au
    // moment du init pour ne pas la perdre.
    const savedCommitmentLockedYearly = useMemo(() => {
        const savedJulyChoice =
            savedJulyStatus === SUMMER_CHOICE_STATUS.CHOSE_PAUSE
                ? SUMMER_MONTH_CHOICE.PAUSE
                : null
        const savedAugustChoice =
            savedAugustStatus === SUMMER_CHOICE_STATUS.CHOSE_PAUSE
                ? SUMMER_MONTH_CHOICE.PAUSE
                : null
        return isCommitmentBeyondSummerProjected(
            projectCommitmentEndDate(
                baseCommitmentEndDate,
                savedJulyChoice,
                savedAugustChoice
            ),
            undefined,
            summerConfig
        )
    }, [
        baseCommitmentEndDate,
        savedJulyStatus,
        savedAugustStatus,
        summerConfig,
    ])

    useEffect(() => {
        if (!pauseBalanceLoaded) return

        const fireWipe = () => {
            props.setSummerResumeConfig({
                type: null,
                frequency: null,
                commitment: null,
                subjectIds: [],
            })
        }

        if (!wipeInitializedRef.current) {
            // Premier rendu post-load — on capture la valeur stabilisée
            // comme baseline. La transition entre la valeur mount-time
            // (basée sur commit_ends_at post-shift) et la valeur post-load
            // (basée sur previous_commitment_end_date) n'est pas une action
            // utilisateur, c'est juste l'hydratation correcte — donc pas de
            // wipe AUTOMATIQUE ici. EXCEPTION : si la baseline "sauvegardé
            // seul" est lockée alors que la projection courante ne l'est
            // plus, c'est qu'une édition draft a atterri pendant le
            // round-trip pauseBalance. On fire le wipe rétroactivement.
            wipeInitializedRef.current = true
            prevCommitmentLockedYearlyRef.current = commitmentLockedYearly
            if (savedCommitmentLockedYearly && !commitmentLockedYearly) {
                fireWipe()
            }
            return
        }

        const wasLocked = prevCommitmentLockedYearlyRef.current
        prevCommitmentLockedYearlyRef.current = commitmentLockedYearly

        if (wasLocked && !commitmentLockedYearly) {
            fireWipe()
        }
        // `savedCommitmentLockedYearly` is intentionally read inside without
        // being a dep — it only matters on the one-shot first-init branch,
        // and we don't want a mid-session change to the saved baseline to
        // retrigger that check (would cause spurious wipes on user refresh).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commitmentLockedYearly, pauseBalanceLoaded])

    // Memoized so its identity changes only when a value does — else a fresh
    // object each render (e.g. a mount data fetch) retriggers the Rentrée
    // modal's reset effect and wipes the parent's in-progress edits.
    const resumeForCard = useMemo(
        () => ({
            type: summer?.resumeType,
            frequency: summer?.resumeFrequency,
            commitment: commitmentLockedYearly
                ? SUMMER_RESUME_COMMITMENT.YEARLY_12M
                : (summer?.resumeCommitment ??
                  SUMMER_RESUME_COMMITMENT.MONTHLY),
            resumeDate: summer?.resumeDate ?? summerCfg.resumeDate,
            subjectIds: summer?.resumeSubjectIds ?? [],
        }),
        [
            summer?.resumeType,
            summer?.resumeFrequency,
            commitmentLockedYearly,
            summer?.resumeCommitment,
            summer?.resumeDate,
            summerCfg.resumeDate,
            summer?.resumeSubjectIds,
        ]
    )

    const handleConfirm = () => {
        // Optimistic state update (pose decidedAt côté Redux) puis push
        // back. Le backend upsert sur (user_id, year) et retourne le
        // UserResource refreshed avec les 3 dimensions persistées.
        props.confirmSummerChoices()
        // Per-month status — chaque mois projette indépendamment son
        // SUMMER_MONTH_CHOICE vers un summer_status backend. Pour les
        // C2 strictement couverts, on force chose_pause (factuel : la
        // pause Stripe déjà programmée couvre les deux mois).
        const julyStatus = isC2FullyCovered
            ? SUMMER_CHOICE_STATUS.CHOSE_PAUSE
            : monthChoiceToSummerStatus(julyChoice)
        const augustStatus = isC2FullyCovered
            ? SUMMER_CHOICE_STATUS.CHOSE_PAUSE
            : monthChoiceToSummerStatus(augustChoice)
        // Aggregate legacy summer_status — gardé en payload "ceinture +
        // bretelles" pour les consumers backend qui n'ont pas encore migré
        // sur le per-month split.
        const summerStatus = isC2FullyCovered
            ? SUMMER_CHOICE_STATUS.CHOSE_PAUSE
            : deriveSummerStatusFromChoices(summer.july, summer.august)
        // Derive resume_preference from the rentrée offer config so the
        // backend cohort routing has the full picture. The Rentrée modal
        // sets resumeType + resumeFrequency without ever calling
        // setResumePreference, so as long as the parent has picked an
        // offer (resumeType set), we re-derive — this keeps re-confirm
        // flows in sync when the parent switches Autonome → Cours
        // partagés on a second pass (stale `summer.resumePreference`
        // would otherwise win). When no resumeType is set yet, fall
        // back on the explicit field to keep any future "Undecided" CTA
        // override addressable.
        const derivedResumePreference = deriveResumePreferenceFromConfig({
            type: summer.resumeType,
            frequency: summer.resumeFrequency,
        })
        const resumePreference = summer.resumeType
            ? derivedResumePreference
            : summer.resumePreference && summer.resumePreference !== "not_set"
              ? summer.resumePreference
              : derivedResumePreference
        // Type d'offre rentrée — explicite côté payload pour lever
        // l'ambiguïté de `resume_preference` (hours_2h peut être partagés
        // OU particuliers selon le `resumeType` sélectionné dans la modale).
        const resumeOfferType = deriveResumeOfferTypeFromConfig({
            type: summer.resumeType,
        })
        // Choix engagement à la rentrée — porté côté Redux (`resumeCommitment`,
        // posé via setSummerResumeConfig). Si l'engagement PROJETÉ
        // (`commitmentLockedYearly`) court au-delà du 31/08, on envoie
        // systématiquement `yearly_12m`. La projection inclut déjà l'effet
        // des pauses locales — quand le parent retire les pauses, le lock
        // tombe naturellement et la valeur Redux remonte.
        const resumeCommitment = commitmentLockedYearly
            ? SUMMER_RESUME_COMMITMENT.YEARLY_12M
            : (summer.resumeCommitment ?? SUMMER_RESUME_COMMITMENT.MONTHLY)
        // Per-month Cours d'été config : on ne push que si le mois est en
        // SUMMER_COURSES (sinon null efface explicitement côté backend, ce
        // qui couvre le cas où le parent re-confirme après avoir basculé
        // Cours d'été → Autonomie/Pause).
        const julyCoursesConfig =
            julyChoice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
            summer.july &&
            typeof summer.july !== "string"
                ? (summer.july.coursesConfig ?? null)
                : null
        const augustCoursesConfig =
            augustChoice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
            summer.august &&
            typeof summer.august !== "string"
                ? (summer.august.coursesConfig ?? null)
                : null
        // June dimension — only `pause_early` (with a date) or `not_set`
        // (default continuation) are wired. Derive from the picked date so
        // the two fields can never diverge.
        const juneTransition = junePauseStartDate
            ? JUNE_TRANSITION.PAUSE_EARLY
            : JUNE_TRANSITION.NOT_SET
        const payload = {
            june_transition: juneTransition,
            june_pause_start_date: junePauseStartDate,
            summer_status: summerStatus,
            july_status: julyStatus,
            august_status: augustStatus,
            resume_preference: resumePreference,
            resume_offer_type: resumeOfferType,
            resume_commitment: resumeCommitment,
            resume_date: summer.resumeDate ?? null,
            july_courses_config: julyCoursesConfig,
            august_courses_config: augustCoursesConfig,
            resume_subject_ids: summer.resumeSubjectIds ?? null,
        }
        // Navigation déférée au success callback de useApiStatus pour
        // garantir la persistence backend avant de fermer l'écran.
        props.apiRequest(subscriptionService.confirmSummerChoice, {}, payload)
    }

    const handleExitRequest = () => {
        // Etat (b) — Brouillon non confirmé : confirmer maintenant ou
        // abandonner. On reset le slice Redux pour que la page Mon
        // abonnement reflète la vérité backend (= aucun choix posé)
        // plutôt que des données locales fantômes.
        if (hasAnyChoice && !summer?.decidedAt) {
            DialogM.show({
                text1: "Confirmer votre trajectoire d'été ?",
                text2: canConfirm
                    ? "Vos choix sont prêts mais pas encore enregistrés. Confirmez maintenant pour les valider, ou quittez pour repartir d'une page blanche."
                    : "Vos choix ne sont pas encore enregistrés et la trajectoire n'est pas complète. Si vous quittez maintenant, votre sélection en cours sera abandonnée — vous pourrez recommencer plus tard.",
                buttonText1: canConfirm
                    ? "Confirmer maintenant"
                    : "Quitter sans enregistrer",
                onPress: () => {
                    if (canConfirm) {
                        handleConfirm()
                    } else {
                        props.resetSummerChoice()
                        navigation.goBack()
                    }
                },
                buttonText3: "Continuer ici",
                onPress3: () => {},
                exitButton: true,
                onPressBack: () => {},
            })
            return
        }
        // Etat (c) — Trajectoire confirmée puis re-éditée localement :
        // une option DESTRUCTIVE (Abandonner) reset le draft au snapshot
        // backend via resetSummerChoice + re-hydration sur la prochaine
        // SUCCESS de authService.getUser. La divergence par axe est
        // calculée plus haut (localDraftDivergesFromSaved).
        if (summer?.decidedAt && localDraftDivergesFromSaved) {
            DialogM.show({
                text1: "Modifications non enregistrées",
                text2: canConfirm
                    ? "Vos changements ne sont pas enregistrés. Confirmez pour les valider, ou abandonnez pour revenir à votre trajectoire précédente."
                    : "Vos changements ne sont pas enregistrés et la trajectoire n'est pas complète. Si vous quittez maintenant, vos modifications seront perdues.",
                buttonText1: canConfirm
                    ? "Confirmer mes modifications"
                    : "Abandonner mes modifications",
                onPress: () => {
                    if (canConfirm) {
                        handleConfirm()
                    } else {
                        props.resetSummerChoice()
                        props.apiRequest(authService.getUser)
                        navigation.goBack()
                    }
                },
                buttonText3: "Continuer ici",
                onPress3: () => {},
                exitButton: true,
                onPressBack: () => {},
            })
            return
        }
        navigation.goBack()
    }

    return (
        <View style={{ flex: 1, backgroundColor: theme.colors.white }}>
            <HeaderContainerM>
                <IconButtonM
                    icon={"times"}
                    iconColor={theme.colors.primary}
                    size={24}
                    style={{ position: "absolute", left: 4 }}
                    onPress={handleExitRequest}
                />
                <TitleM>
                    Été {summerYear} — {firstName}
                </TitleM>
                <IconButtonM
                    icon={"question-circle"}
                    iconColor={theme.colors.primary}
                    size={22}
                    style={{ position: "absolute", right: 4 }}
                    onPress={() => setFaqVisible(true)}
                />
            </HeaderContainerM>

            <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={styles.scrollContent}
            >
                <View
                    style={{
                        width: contentWidth,
                        alignSelf: "center",
                        paddingHorizontal: 20,
                    }}
                >
                    <View style={styles.heroWrap}>
                        <TextM fontSize={14} color={theme.colors.grey} wrap>
                            Faites votre choix pour l&apos;été et la reprise — 3
                            décisions à prendre.
                        </TextM>
                    </View>

                    {beforeSummerWindow && (
                        <JuneBlock
                            currentFormulaLabel={currentFormulaLabel}
                            junePauseStartDate={junePauseStartDate}
                            juneEndLabel={juneMaxDate.format("D MMMM")}
                            canAdvancePause={canAdvanceJunePause}
                            locked={juneChoiceLocked}
                            onAdvancePause={openJunePicker}
                            onEditPause={openJunePicker}
                            onCancelPause={cancelJunePause}
                        />
                    )}

                    {(!julyEligible || !augustEligible) &&
                    !julyForcedReason &&
                    !augustForcedReason ? (
                        <View style={styles.pauseOverlapInfo}>
                            <FontAwesome5
                                name={"pause-circle"}
                                solid
                                size={14}
                                color={theme.colors.primary}
                                style={{ marginRight: 8 }}
                            />
                            <TextM fontSize={12} wrap style={{ flex: 1 }}>
                                {plannedResumeDate
                                    ? `Une pause est en cours — reprise prévue le ${moment(
                                          plannedResumeDate
                                      ).format(
                                          "DD MMMM YYYY"
                                      )}. Seuls les mois ouverts à la reprise sont configurables ici.`
                                    : "Une pause est en cours — seuls les mois ouverts à la reprise sont configurables ici."}
                            </TextM>
                        </View>
                    ) : null}

                    <View style={[styles.cardsRow, styles.cardsRowMobile]}>
                        {julyEligible || julyForcedReason ? (
                            <View
                                style={{
                                    width: cardWidth,
                                    marginBottom: CARD_GAP,
                                }}
                            >
                                <SummerMonthPlanCard
                                    month={SUMMER_MONTHS.JULY}
                                    slot={summer.july}
                                    onEdit={openJuly}
                                    schoolSubjects={props.schoolSubjects}
                                    user={props.user}
                                    summerConfig={summerConfig}
                                    locked={julyLocked}
                                    forcedReadOnlyReason={julyForcedReason}
                                />
                            </View>
                        ) : null}
                        {augustEligible || augustForcedReason ? (
                            <View
                                style={{
                                    width: cardWidth,
                                    marginBottom: CARD_GAP,
                                }}
                            >
                                <SummerMonthPlanCard
                                    month={SUMMER_MONTHS.AUGUST}
                                    slot={summer.august}
                                    onEdit={openAugust}
                                    schoolSubjects={props.schoolSubjects}
                                    user={props.user}
                                    summerConfig={summerConfig}
                                    locked={augustLocked}
                                    forcedReadOnlyReason={augustForcedReason}
                                />
                            </View>
                        ) : null}
                        <View
                            style={{
                                width: cardWidth,
                                marginBottom: CARD_GAP,
                            }}
                        >
                            <SummerResumePlanCard
                                resume={resumeForCard}
                                onEdit={openResume}
                                pricingTable={props.pricingTable}
                                sessionsPerWeek={props.sessionsPerWeek}
                                schoolSubjects={props.schoolSubjects}
                                renewalDiscountEligible={
                                    renewalDiscountEligible
                                }
                            />
                        </View>
                    </View>

                    {showCommitmentGift ? (
                        <View style={styles.giftBox}>
                            <FontAwesome5
                                name={"gift"}
                                solid
                                size={16}
                                color={theme.colors.success}
                                style={{ marginRight: 10 }}
                            />
                            <TextM fontSize={13} wrap style={{ flex: 1 }}>
                                🎁 {COMMITMENT_HALF}€ offerts au 1er mois +{" "}
                                {COMMITMENT_HALF}€ au 2e mois grâce à
                                l&apos;engagement 12 mois.
                            </TextM>
                        </View>
                    ) : null}

                    <View style={styles.confirmWrap}>
                        <SunGradientButton
                            onPress={handleConfirm}
                            disabled={!canConfirm || props.confirming}
                            loading={props.confirming}
                            label={
                                canConfirm
                                    ? "Confirmer ma trajectoire d'été"
                                    : "Configurez d'abord juillet, août et la rentrée"
                            }
                        />
                        {!canConfirm ? (
                            <TextM
                                fontSize={11}
                                color={theme.colors.grey}
                                wrap
                                center
                                style={{ marginTop: 8 }}
                            >
                                Cliquez sur « Modifier » dans chacune des cartes
                                ci-dessus pour configurer vos choix.
                            </TextM>
                        ) : null}
                    </View>
                </View>
            </ScrollView>

            <SummerMonthEditModal
                month={SUMMER_MONTHS.JULY}
                bottomSheetRef={julyRef}
                initialSlot={summer.july}
                defaultSubjectIds={defaultSubjectIds}
                onValidate={({ choice, coursesConfig }) =>
                    props.setSummerMonthChoice(
                        SUMMER_MONTHS.JULY,
                        choice,
                        coursesConfig
                    )
                }
                onClose={() => julyRef.current?.dismiss?.()}
            />
            <SummerMonthEditModal
                month={SUMMER_MONTHS.AUGUST}
                bottomSheetRef={augustRef}
                initialSlot={summer.august}
                defaultSubjectIds={defaultSubjectIds}
                onValidate={({ choice, coursesConfig }) =>
                    props.setSummerMonthChoice(
                        SUMMER_MONTHS.AUGUST,
                        choice,
                        coursesConfig
                    )
                }
                onClose={() => augustRef.current?.dismiss?.()}
            />
            <SummerResumeEditModal
                bottomSheetRef={resumeRef}
                initialResume={resumeForCard}
                defaultSubjectIds={defaultSubjectIds}
                projectedCommitmentEndDate={projectedCommitmentEndDate}
                commitmentLockedYearlyOverride={commitmentLockedYearly}
                onValidate={(payload) => props.setSummerResumeConfig(payload)}
                onClose={() => resumeRef.current?.dismiss?.()}
            />

            <FaqModal
                visible={faqVisible}
                onClose={() => setFaqVisible(false)}
            />

            <MobileDatePickerModal
                visible={junePickerVisible}
                onDismiss={() => setJunePickerVisible(false)}
                value={junePauseStartDate}
                validRange={{
                    startDate: juneMinDate.toDate(),
                    endDate: juneMaxDate.toDate(),
                }}
                onConfirm={confirmJunePause}
                label={"Avancer ma pause en juin"}
            />
        </View>
    )
}

// June cockpit block — read-only continuation of the current formula by
// default ("Juin — poursuite de [formule] jusqu'au 30 juin"), with a discreet
// text button to optionally advance a pause. When a date is picked the card
// flips to "Pause à partir du XX juin" with modify / cancel affordances.
// Deliberately NOT a third choice column alongside July/August — it never
// enters the confirm gating.
const JuneBlock = ({
    currentFormulaLabel,
    junePauseStartDate,
    juneEndLabel,
    canAdvancePause,
    locked,
    onAdvancePause,
    onEditPause,
    onCancelPause,
}) => {
    const hasJunePause = !!junePauseStartDate
    const formattedDate = hasJunePause
        ? moment(junePauseStartDate).format("D MMMM")
        : null
    return (
        <View style={styles.juneBlock}>
            <View style={styles.juneHeaderRow}>
                <FontAwesome5
                    name={hasJunePause ? "pause-circle" : "calendar-check"}
                    solid
                    size={14}
                    color={theme.colors.primary}
                    style={{ marginRight: 8 }}
                />
                <TextM
                    fontWeight={"Bold"}
                    fontSize={12}
                    color={theme.colors.grey}
                    uppercase
                    wrap
                    style={{ letterSpacing: 0.4, flex: 1 }}
                >
                    Juin
                </TextM>
            </View>
            {hasJunePause ? (
                <>
                    <TextM fontSize={13} wrap style={{ marginTop: 2 }}>
                        Pause à partir du {formattedDate}
                    </TextM>
                    {/* Once the pause has started its date is fixed — show a
                        read-only note instead of the modify / cancel actions. */}
                    {locked ? (
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            wrap
                            style={{ marginTop: 4 }}
                        >
                            Pause en cours — modification impossible.
                        </TextM>
                    ) : (
                        <View style={styles.juneActionsRow}>
                            {canAdvancePause ? (
                                <TextButtonM
                                    label={"Modifier"}
                                    fontSize={12}
                                    onPress={onEditPause}
                                    style={{ width: null, marginRight: 16 }}
                                />
                            ) : null}
                            <TextButtonM
                                label={"Annuler"}
                                fontSize={12}
                                color={theme.colors.grey}
                                onPress={onCancelPause}
                                style={{ width: null }}
                            />
                        </View>
                    )}
                </>
            ) : (
                <>
                    <TextM
                        fontSize={13}
                        color={theme.colors.grey}
                        wrap
                        style={{ marginTop: 2 }}
                    >
                        Poursuite de {currentFormulaLabel} jusqu&apos;au{" "}
                        {juneEndLabel}.
                    </TextM>
                    {canAdvancePause ? (
                        <TextButtonM
                            label={"Avancer ma pause en juin"}
                            fontSize={12}
                            onPress={onAdvancePause}
                            style={{ width: null, marginTop: 2 }}
                        />
                    ) : null}
                </>
            )}
        </View>
    )
}

// Wrapped with the shared withPreventDoubleClick HOC (same guard as
// FilledButtonM): a 300ms leading-edge debounce on onPress so an accidental
// rapid double-tap on "Confirmer" fires a single submit. Coarser double-taps /
// multi-device submits are caught server-side by the per-user idempotency lock
// on the summer-choice endpoint.
const SunGradientButton = withPreventDoubleClick(({ label, onPress, disabled, loading }) => (
    <TouchableOpacity
        onPress={disabled ? undefined : onPress}
        activeOpacity={disabled ? 1 : 0.85}
        style={[styles.gradientWrap, disabled && { opacity: 0.5 }]}
    >
        <LinearGradient
            colors={["#FBBF24", "#FB923C", "#EA580C"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.gradientInner}
        >
            {loading ? (
                <ActivityIndicator
                    size="small"
                    color={theme.colors.white}
                    style={{ marginRight: 10 }}
                />
            ) : (
                <FontAwesome5
                    name={"sun"}
                    solid
                    size={16}
                    color={theme.colors.white}
                    style={{ marginRight: 10 }}
                />
            )}
            <TextM
                fontWeight={"ExtraBold"}
                fontSize={15}
                color={theme.colors.white}
                wrap
                center
            >
                {loading ? "Enregistrement…" : label}
            </TextM>
        </LinearGradient>
    </TouchableOpacity>
))

const FaqModal = ({ visible, onClose }) => (
    <ModalSkeleton
        isVisible={visible}
        onClose={onClose}
        title={"Questions fréquentes"}
    >
        <View style={{ gap: 14 }}>
            <List.AccordionGroup>
                {FAQ_ITEMS.map((item, index) => (
                    <List.Accordion
                        key={index}
                        id={index.toString()}
                        title={item.title}
                        titleNumberOfLines={3}
                        titleStyle={{
                            fontFamily: "Montserrat-SemiBold",
                            fontSize: 13,
                            color: theme.colors.primary,
                            flexShrink: 1,
                        }}
                        left={() => (
                            <View style={styles.faqIcon}>
                                <FontAwesome5
                                    name={item.icon}
                                    size={16}
                                    color={theme.colors.primary}
                                    solid
                                />
                            </View>
                        )}
                        style={styles.faqItem}
                        contentStyle={{ paddingRight: 8 }}
                    >
                        <View style={styles.faqContent}>
                            <TextM color={theme.colors.grey} fontSize={13} wrap>
                                {item.body}
                            </TextM>
                        </View>
                    </List.Accordion>
                ))}
            </List.AccordionGroup>
        </View>
    </ModalSkeleton>
)

const styles = StyleSheet.create({
    scrollContent: {
        paddingTop: 16,
        paddingBottom: 40,
    },
    heroWrap: {
        width: "100%",
        marginBottom: 20,
    },
    juneBlock: {
        width: "100%",
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        padding: 14,
        marginBottom: 16,
    },
    juneHeaderRow: {
        flexDirection: "row",
        alignItems: "center",
    },
    juneActionsRow: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: 4,
    },
    cardsRow: {
        width: "100%",
    },
    cardsRowDesktop: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: CARD_GAP,
    },
    cardsRowMobile: {
        flexDirection: "column",
    },
    pauseOverlapInfo: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.lightMoreTransparent,
        padding: 12,
        borderRadius: 10,
        marginBottom: 16,
    },
    giftBox: {
        marginTop: 18,
        backgroundColor: theme.colors.successLight || theme.colors.greyBg,
        borderRadius: 10,
        padding: 12,
        flexDirection: "row",
        alignItems: "center",
        borderWidth: 1,
        borderColor: theme.colors.success,
    },
    confirmWrap: {
        marginTop: 24,
        width: "100%",
    },
    gradientWrap: {
        width: "100%",
        borderRadius: 14,
        overflow: "hidden",
        shadowColor: "#000",
        shadowOpacity: 0.15,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
        elevation: 4,
    },
    gradientInner: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 14,
        paddingHorizontal: 18,
    },
    faqIcon: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor:
            theme.colors.material?.primary?.["50"] ||
            theme.colors.primaryLighter ||
            theme.colors.lightMoreTransparent,
        justifyContent: "center",
        alignItems: "center",
        marginLeft: 4,
    },
    faqItem: {
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 8,
        marginBottom: 8,
        overflow: "hidden",
        paddingVertical: 4,
    },
    faqContent: {
        paddingLeft: 12,
        paddingRight: 16,
        paddingBottom: 12,
        paddingTop: 0,
        backgroundColor: theme.colors.white,
    },
})

// `currentUser.object` is the source of truth for the summer_* axes the
// cockpit reads. `formData.user` is the legacy normalized shape and may
// not carry them (or may carry stale ones). We spread `formData.user`
// last so its richer fields win in general, then re-pin the summer axes
// from `currentUser.object` so they can never be silently shadowed.
//
// Note: when `currentUser.object` is still empty (pre-hydration),
// `pickSummerAxes` returns `{}` and the summer axes fall through to
// `formData.user` — the cockpit is reached after `getSubscribePricing`
// has populated `formData.user`, so the parent always sees a coherent
// snapshot.
const SUMMER_AXES_FROM_CURRENT_USER = [
    "summer_july_status",
    "summer_august_status",
    "summer_june_transition",
    "summer_june_pause_start_date",
    "summer_resume_preference",
    "summer_resume_offer_type",
    "summer_resume_commitment",
]

const pickSummerAxes = (source) =>
    Object.fromEntries(
        SUMMER_AXES_FROM_CURRENT_USER.filter(
            (k) => source[k] !== undefined
        ).map((k) => [k, source[k]])
    )

const mapStateToProps = (state) => {
    const currentUser = state.data.currentUser?.object || {}
    return {
        user: {
            ...currentUser,
            ...(state.data.formData?.user || {}),
            ...pickSummerAxes(currentUser),
        },
        summer: state.data.summer,
        schoolSubjects: state.data.formData?.schoolSubjects,
        pricingTable: state.data.formData?.pricingTableV2,
        sessionsPerWeek: state.data.formData?.sessionsPerWeek,
        summerYear: getSummerYearFromStaticData(state.data.staticData),
        summerConfig: state.data.staticData?.summer_config,
        confirming:
            state.api?.[subscriptionService.confirmSummerChoice.name]?.request,
        // Hydratée par getParentPauseBalance (mount + post-confirm). Sert au
        // safety check Case 3 (`commitmentLockIsPauseInduced`).
        pauseBalance: state.data.formData?.pauseBalance,
    }
}

const mapDispatchToProps = {
    apiRequest,
    markSummerCockpitViewed,
    setSummerMonthChoice,
    setSummerResumeConfig,
    setJunePause,
    confirmSummerChoices,
    resetSummerChoice,
}

/**
 * Porte de la route `summer-cockpit`. Hors édition (interrupteur saisonnier
 * `summer_config.cockpit_enabled`), l'écran n'est PAS monté : un lien direct,
 * un favori ou un onglet resté ouvert depuis l'été atterrit sur la page
 * Abonnement, qui porte désormais la trajectoire de reprise.
 *
 * Ne pas le monter est le point, plutôt que de rediriger depuis l'intérieur :
 * son effet de montage marque la visite en Redux et tire deux requêtes, et le
 * cockpit s'afficherait le temps d'un rendu avant de disparaître. Même raison
 * pour attendre `staticData` : sans le payload, `getSummerConfigSafe` répond
 * « allumé » par défaut, et on monterait sur une réponse qu'on n'a pas encore.
 *
 * En mode élève, `menu` porte les onglets élève et non ceux du parent : la
 * redirection y atterrit sur la home élève. Vérifié en recette — pas d'erreur
 * de navigation, pas d'écran blanc.
 */
const SummerCockpitRoute = (props) => {
    const navigation = useNavigation()
    const configLoaded = !!props.summerConfig
    const { cockpitEnabled } = getSummerConfigSafe(props.summerConfig)
    const shouldRedirect = configLoaded && !cockpitEnabled

    useEffect(() => {
        if (!shouldRedirect) return
        // `parent-subscription` est un onglet du navigateur parent, pas une
        // route de cette pile : viser l'onglet directement laisse une page
        // blanche (« NAVIGATE … was not handled by any navigator »). On passe
        // par `menu`, l'écran racine qui porte le navigateur d'onglets — même
        // idiome que le flux de résiliation.
        navigation.navigate("menu", { screen: "parent-subscription" })
    }, [shouldRedirect, navigation])

    if (!configLoaded || !cockpitEnabled) return null

    return <SummerCockpitScreen {...props} />
}

export default connect(mapStateToProps, mapDispatchToProps)(SummerCockpitRoute)
